import type { AppClientDatabase } from '@app/icare/client/db/schema'
import type { ClientTableName } from '@app/icare/client/db/types'
import { count, executePaginated, type QueryParams } from '@pkg/db/query'
import { type DefaultError, environmentManager, queryOptions, type UseQueryOptions } from '@tanstack/react-query'
import type { SelectQueryBuilder, Simplify } from 'kysely'

/**
 * Tables a query reads from. Used by the cache-invalidation hook to build a
 * `table → queryHash` index. Optional — when omitted, the SQL is parsed for
 * `from "X"` / `join "X"` references. Override required for view-based or
 * raw-SQL queries (e.g. `patientFindingsView`) where the parser can't see
 * the underlying tables.
 */
export type LocalQueryTables = readonly ClientTableName[]

export interface LocalQueryScope {
  patientId?: string
  relationId?: string
  tags?: readonly string[]
}

export interface LocalQueryMeta {
  /** Tables this query reads from. Authoritative when set; falls back to SQL parsing. */
  tables?: LocalQueryTables
  /** Compiled SQL used by the cache-invalidation index to derive tables when `tables` is omitted. */
  sql?: string
  /** Raw SQL parameters; consumed by the `icareSql()` DevTools helper to inline literals. */
  parameters?: readonly unknown[]
  /** Optional scope for targeted invalidation (e.g. patient-bound relation screens). */
  scope?: LocalQueryScope
}

// ── Shared types ───────────────────────────────────────────────────────────

export interface QueryResult<R> {
  data: NonNullable<R>[]
  total: number
  current?: number
  page?: number
  pageSize?: number
  nextPageCursor?: string | null
}

export const LocalQueryType = {
  paginated: 'paginated',
  result: 'result',
  count: 'count',
} as const

export type LocalQueryType = (typeof LocalQueryType)[keyof typeof LocalQueryType]

export type ResultTypeFromQueryResult<TQueryResult> = TQueryResult extends QueryResult<infer R> ? R : never

// ── Paginated ──────────────────────────────────────────────────────────────

interface PaginatedOpts<O, TQueryFnData, TError, TData>
  extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> {
  query: SelectQueryBuilder<AppClientDatabase, never, O>
  params: QueryParams
  tables?: LocalQueryTables
}

export const localPaginatedQueryOptions = <
  O,
  TQueryFnData extends QueryResult<Simplify<O>> = QueryResult<Simplify<O>>,
  TError = DefaultError,
  TData = TQueryFnData,
>(
  opts: PaginatedOpts<O, TQueryFnData, TError, TData>,
) => {
  const { query, params, tables, meta, ...rest } = opts
  const { sql, parameters } = query.compile()
  const keyParams = JSON.stringify(parameters)

  return queryOptions<TQueryFnData, TError, TData>({
    queryKey: ['app', 'query', 'paginated', sql, keyParams],
    meta: { ...(meta ?? {}), tables, sql, parameters } satisfies LocalQueryMeta,
    ...rest,
    queryFn: async () => {
      if (environmentManager.isServer()) {
        return { data: [], total: 0 } as never as TQueryFnData
      }
      const result = await executePaginated(query as never, params)
      return { ...result, current: result.page, pageSizeOptions: [10, 20, 50, 100] } as never as TQueryFnData
    },
  } as never)
}

// ── Result (array) ─────────────────────────────────────────────────────────

interface ResultOpts<O, TQueryFnData, TError, TData>
  extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> {
  query: SelectQueryBuilder<AppClientDatabase, never, O>
  tables?: LocalQueryTables
}

export const localResultQueryOptions = <
  O,
  TQueryFnData extends Simplify<O>[] = Simplify<O>[],
  TError = DefaultError,
  TData = TQueryFnData,
>(
  opts: ResultOpts<O, TQueryFnData, TError, TData>,
) => {
  const { query, tables, meta, ...rest } = opts
  const { sql, parameters } = query.compile()
  const keyParams = JSON.stringify(parameters)

  return queryOptions<TQueryFnData, TError, TData>({
    queryKey: ['app', 'query', 'result', sql, keyParams],
    meta: { ...(meta ?? {}), tables, sql, parameters } satisfies LocalQueryMeta,
    ...rest,
    queryFn: () => {
      if (environmentManager.isServer()) return [] as never
      return query.execute() as never
    },
  } as never)
}

// ── Result one ─────────────────────────────────────────────────────────────

interface ResultOneOpts<O, TQueryFnData, TError, TData>
  extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> {
  query: SelectQueryBuilder<AppClientDatabase, never, O>
  tables?: LocalQueryTables
}

export const localResultOneQueryOptions = <
  O,
  TQueryFnData extends Simplify<O> = Simplify<O>,
  TError = DefaultError,
  TData = TQueryFnData,
>(
  opts: ResultOneOpts<O, TQueryFnData, TError, TData>,
) => {
  const { query, tables, meta, ...rest } = opts
  const { sql, parameters } = query.compile()
  const keyParams = JSON.stringify(parameters)

  return queryOptions<TQueryFnData, TError, TData>({
    queryKey: ['app', 'query', 'result-one', sql, keyParams],
    meta: { ...(meta ?? {}), tables, sql, parameters } satisfies LocalQueryMeta,
    ...rest,
    queryFn: () => {
      if (environmentManager.isServer()) return null as never
      return query.executeTakeFirst() as never
    },
  } as never)
}

// ── Count ──────────────────────────────────────────────────────────────────

interface CountOpts<O, TQueryFnData, TError, TData>
  extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> {
  query: SelectQueryBuilder<AppClientDatabase, never, O>
  tables?: LocalQueryTables
}

export const localCountQueryOptions = <
  O,
  TQueryFnData extends number = number,
  TError = DefaultError,
  TData = TQueryFnData,
>(
  opts: CountOpts<O, TQueryFnData, TError, TData>,
) => {
  const { query, tables, meta, ...rest } = opts
  const { sql, parameters } = query.compile()
  const keyParams = JSON.stringify(parameters)

  return queryOptions<TQueryFnData, TError, TData>({
    queryKey: ['app', 'query', 'count', sql, keyParams],
    meta: { ...(meta ?? {}), tables, sql, parameters } satisfies LocalQueryMeta,
    ...rest,
    queryFn: () => {
      if (environmentManager.isServer()) return 0 as never as TQueryFnData
      return count(query as never) as never as TQueryFnData
    },
  } as never)
}

// ── Polymorphic dispatcher ─────────────────────────────────────────────────

type LocalQueryOptionsWithType<TType extends LocalQueryType, O, TQueryFnData, TError, TData> = TType extends 'paginated'
  ? PaginatedOpts<O, TQueryFnData, TError, TData> & { type: 'paginated' }
  : TType extends 'result'
    ? ResultOpts<O, TQueryFnData, TError, TData> & { type: 'result' }
    : CountOpts<O, TQueryFnData, TError, TData> & { type: 'count' }

export function localQueryOptions<
  O,
  TQueryFnData extends QueryResult<Simplify<O>>,
  TError = DefaultError,
  TData = TQueryFnData,
>(
  opts: PaginatedOpts<O, TQueryFnData, TError, TData> & { type: 'paginated' },
): ReturnType<typeof localPaginatedQueryOptions<O, TQueryFnData, TError, TData>>

export function localQueryOptions<O, TQueryFnData extends Simplify<O>[], TError = DefaultError, TData = TQueryFnData>(
  opts: ResultOpts<O, TQueryFnData, TError, TData> & { type: 'result' },
): ReturnType<typeof localResultQueryOptions<O, TQueryFnData, TError, TData>>

export function localQueryOptions<O, TQueryFnData extends number, TError = DefaultError, TData = TQueryFnData>(
  opts: CountOpts<O, TQueryFnData, TError, TData> & { type: 'count' },
): ReturnType<typeof localCountQueryOptions<O, TQueryFnData, TError, TData>>

export function localQueryOptions<T extends LocalQueryType, O, TQueryFnData, TError, TData>(
  opts: LocalQueryOptionsWithType<T, O, TQueryFnData, TError, TData>,
) {
  const { type, ...rest } = opts as never as { type: T } & Record<string, unknown>
  switch (type) {
    case LocalQueryType.paginated:
      return localPaginatedQueryOptions(rest as never)
    case LocalQueryType.result:
      return localResultQueryOptions(rest as never)
    case LocalQueryType.count:
      return localCountQueryOptions(rest as never)
    default:
      throw new Error(`Unknown query type: ${type as string}`)
  }
}
